import {
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	UsePointerEventOptions,
	usePointerEventOutside,
	useResizeObserver
} from "../../Hooks";
import {
	clamp,
	isNotNil
} from "../../Misc/utils";
import { mergeRefs } from "client/Misc/reactUtils";
import { Coordinate } from "../../Misc/commonTypes";
import { Portal } from "../Portal/Portal";
import styles from "./DropOver.module.less";
import {
	AbsoluteCoordinate,
	DropOverAlignmentValue,
	DropOverContextData,
	DropOverProps,
	DescendantRootsRegistration,
	DropOverContext,
	VerticalAlignmentValue,
	HorizontalAlignmentValue,
	RectangleTargetAlignment
} from "./DropOver";
import { never } from "client_legacy/ndaw/errors";
import { useOnCurrentWindowBlur } from "./useOnCurrentWindowBlur";
import { useTargetTracking } from "./useTargetTracking";

const container: HTMLElement = document.body;

export const DropOverDesktop = (props: PropsWithChildren<DropOverProps & { innerRef: Ref<HTMLDivElement>; }>): ReactElement => {
	const {
		target,
		targetAlignment = DEFAULT_TARGET_ALIGNMENT,
		contentAlignment = "tl",
		onClose,
		bubbleClick,
		children,
		innerRef,
		outsideClickIgnoreRefs,
		closeOnMouseDown = false,
		bubbleContextMenu = true,
		ariaMultiselectable,
		id,
		role,
		ariaLabel,
		onKeyUp,
		shouldCloseWhenWindowLoosesFocus = false,
		shouldStayOpenOnScroll = false
	} = props;

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const containerRef: Ref<HTMLElement> = useRef<HTMLElement>(container);

	const availableMaxHeight: RefObject<number | undefined> = useRef<number>(undefined);
	const latchedFlips: RefObject<AlignmentFlips> = useRef<AlignmentFlips>(NO_FLIPS);

	const {
		targetRect: trackedTargetRect,
		isTargetVisible
	} = useTargetTracking(target, shouldStayOpenOnScroll);

	const {
		width,
		height,
		wasInitialized: wasContentResizeObserverInitialized
	} = useResizeObserver(ref);

	const {
		width: containerWidthFromResizeObserver,
		height: containerHeightFromResizeObserver,
		wasInitialized: wasContainerResizeObserverInitialized
	} = useResizeObserver(containerRef);

	const containerWidth: number = wasContainerResizeObserverInitialized
		? containerWidthFromResizeObserver
		: container.clientWidth;

	const containerHeight: number = wasContainerResizeObserverInitialized
		? containerHeightFromResizeObserver
		: container.clientHeight;

	const [descendantsRootRegistrations, setDescendantsRootRegistration] =
		useState<readonly DescendantRootsRegistration[]>([]);

	const parentContext: DropOverContextData = useContext(DropOverContext);

	const otherRoots = useMemo(
		(): readonly HTMLElement[] => {
			const result: HTMLElement[] = outsideClickIgnoreRefs
				?.map((ref) => ref.current)
				?.filter(isNotNil) ?? [];

			for (const registration of descendantsRootRegistrations) {
				result.push(
					...registration
						.roots
						.filter(isNotNil)
				);
			}

			return result;
		},
		[descendantsRootRegistrations, outsideClickIgnoreRefs]
	);

	useEffect(
		(): (() => void) | undefined => {
			const roots: (HTMLElement | nil)[] = [
				ref.current,
				...otherRoots
			];

			return parentContext.registerDescendantsRoots?.(roots);
		},
		[otherRoots, parentContext]
	);

	const pointerEventOptions = useMemo(
		(): UsePointerEventOptions => {
			return {
				otherRoots,
				shouldListenForEntireClick: !closeOnMouseDown,
				ignoreWheelEvent: shouldStayOpenOnScroll,
				ignoreTouchMoveEvent: shouldStayOpenOnScroll
			};
		},
		[
			otherRoots,
			closeOnMouseDown,
			shouldStayOpenOnScroll
		]
	);

	usePointerEventOutside(
		onClose,
		ref.current,
		pointerEventOptions
	);

	useOnCurrentWindowBlur(
		shouldCloseWhenWindowLoosesFocus
			? onClose
			: undefined
	);

	const position: Coordinate = useMemo(
		() => {
			const targetRect: DOMRect = trackedTargetRect ?? target.getBoundingClientRect();

			if (typeof targetAlignment !== "string" && targetAlignment.kind === "absolute") {
				const absolutePosition: Coordinate = getContentPosition({
					targetRect,
					targetAlignment,
					contentAlignment,
					width,
					height
				});

				return {
					x: clamp(
						absolutePosition.x,
						0,
						containerWidth - width
					),
					y: clamp(
						absolutePosition.y,
						0,
						containerHeight - height
					)
				};
			}

			// NOTE: while the DropOver follows a scrolling target we keep the side it was flipped to
			// instead of recomputing it from the props on every scroll step, otherwise it would flip
			// back and forth as the target travels through the viewport.
			const startingFlips: AlignmentFlips = shouldStayOpenOnScroll
				? latchedFlips.current
				: NO_FLIPS;

			let effectiveTargetAlignment: DropOverAlignmentValue | RectangleTargetAlignment = targetAlignment;
			let effectiveContentAlignment: DropOverAlignmentValue = contentAlignment;

			if (startingFlips.isVertical) {
				effectiveTargetAlignment = flipVertical(effectiveTargetAlignment);
				effectiveContentAlignment = flipVertical(effectiveContentAlignment);
			}

			if (startingFlips.isHorizontal) {
				effectiveTargetAlignment = flipHorizontal(effectiveTargetAlignment);
				effectiveContentAlignment = flipHorizontal(effectiveContentAlignment);
			}

			const options: GetContentPositionOptions = {
				targetRect,
				targetAlignment: effectiveTargetAlignment,
				contentAlignment: effectiveContentAlignment,
				width,
				height
			};

			const position: Coordinate = getContentPosition(options);

			let hasFlippedVertically: boolean = false;
			let hasFlippedHorizontally: boolean = false;
			let doesOverflowVertically: boolean = false;

			if (
				!doesFitContainerVertically(
					containerHeight,
					height,
					position.y
				)
			) {
				const flippedContentAlignment: DropOverAlignmentValue = flipVertical(effectiveContentAlignment);
				const flippedTargetAlignment: DropOverAlignmentValue | RectangleTargetAlignment = flipVertical(effectiveTargetAlignment);

				const flippedPositionY: number = getContentPosition({
					...options,
					contentAlignment: flippedContentAlignment,
					targetAlignment: flippedTargetAlignment
				}).y;

				if (
					doesFitContainerVertically(
						containerHeight,
						height,
						flippedPositionY
					)
				) {
					position.y = flippedPositionY;
					hasFlippedVertically = true;
				} else {
					doesOverflowVertically = true;

					const originalMaxHeight: number = getAvailableHeight({
						targetRect,
						targetAlignment: effectiveTargetAlignment,
						contentAlignment: effectiveContentAlignment,
						positionY: position.y,
						containerHeight
					});

					const flippedMaxHeight: number = getAvailableHeight({
						targetRect,
						targetAlignment: flippedTargetAlignment,
						contentAlignment: flippedContentAlignment,
						positionY: flippedPositionY,
						containerHeight
					});

					if (originalMaxHeight < flippedMaxHeight) {
						position.y = flippedPositionY;
						hasFlippedVertically = true;
					}
				}
			}

			// NOTE: derived from the target position rather than from whether the content fits, otherwise
			// applying the constraint makes the content fit, which drops the constraint, which makes it
			// stop fitting again.
			if (
				doesOverflowVertically
				|| (shouldStayOpenOnScroll && wasContentResizeObserverInitialized)
			) {
				const availableHeight: number = getAvailableHeight({
					targetRect,
					targetAlignment: hasFlippedVertically
						? flipVertical(effectiveTargetAlignment)
						: effectiveTargetAlignment,
					contentAlignment: hasFlippedVertically
						? flipVertical(effectiveContentAlignment)
						: effectiveContentAlignment,
					positionY: position.y,
					containerHeight
				});

				availableMaxHeight.current = Math.min(availableHeight, containerHeight);
			}

			if (
				!doesFitContainerHorizontally(
					containerWidth,
					width,
					position.x
				)
			) {
				const potentialX: number = getContentPosition({
					...options,
					contentAlignment: flipHorizontal(effectiveContentAlignment),
					targetAlignment: flipHorizontal(effectiveTargetAlignment)
				}).x;

				if (
					doesFitContainerHorizontally(
						containerWidth,
						width,
						potentialX
					)
				) {
					position.x = potentialX;
					hasFlippedHorizontally = true;
				}
			}

			if (shouldStayOpenOnScroll) {
				latchedFlips.current = {
					isVertical: startingFlips.isVertical !== hasFlippedVertically,
					isHorizontal: startingFlips.isHorizontal !== hasFlippedHorizontally
				};
			}

			return {
				x: clamp(
					position.x,
					0,
					containerWidth - width
				),
				y: clamp(
					position.y,
					0,
					containerHeight - height
				)
			};
		},
		[
			containerHeight,
			containerWidth,
			contentAlignment,
			height,
			shouldStayOpenOnScroll,
			target,
			targetAlignment,
			trackedTargetRect,
			wasContentResizeObserverInitialized,
			width
		]
	);

	const zIndex: number = findMaxZIndex(target, container);
	const isAbleToCorrectlyDetermineContentOffset: boolean = width !== 0 || height !== 0;

	const contentContext: DropOverContextData = useMemo(
		(): DropOverContextData => ({
			onClose,
			registerDescendantsRoots: (roots) => {
				const registration: DescendantRootsRegistration = {
					roots: roots
				};
				setDescendantsRootRegistration(x => [...x, registration]);

				return (): void => {
					setDescendantsRootRegistration(
						s => s.filter(x => x !== registration)
					);
				};
			}
		}),
		[onClose]
	);

	const contextMenuHandler = useMemo(
		(): ((args: MouseEvent) => void) | undefined => {
			if (bubbleContextMenu === false) {
				return (args: MouseEvent): void => {
					args.stopPropagation();
				};
			} else {
				return undefined;
			}
		},
		[bubbleContextMenu]
	);

	return (
		<Portal container={container}>
			<div
				className={styles.root}
				style={{
					zIndex: zIndex + 10
				}}
				onClick={e => {
					if (bubbleClick === false) {
						e.stopPropagation();
					}
				}}
				onContextMenu={contextMenuHandler}
				aria-multiselectable={ariaMultiselectable}
				id={id}
				role={role}
				aria-label={ariaLabel}
				onKeyUp={onKeyUp}
				tabIndex={-1}
			>
				<div
					className={styles.content}
					style={{
						top: isAbleToCorrectlyDetermineContentOffset ? position.y : 0,
						left: isAbleToCorrectlyDetermineContentOffset ? position.x : 0,
						opacity: isAbleToCorrectlyDetermineContentOffset ? undefined : 0,
						visibility: isTargetVisible ? undefined : "hidden",
						pointerEvents: isAbleToCorrectlyDetermineContentOffset && isTargetVisible
							? undefined
							: "none",
						"--container-max-height": isNotNil(availableMaxHeight.current)
							? `${availableMaxHeight.current}px`
							: "none"
					}}
					ref={mergeRefs(ref, innerRef)}
				>
					<DropOverContext.Provider value={contentContext}>
						{children}
					</DropOverContext.Provider>
				</div>
			</div>
		</Portal>
	);
};

DropOverDesktop.displayName = "DropOverDesktop";

function findMaxZIndex(target: HTMLElement, container: HTMLElement): number {
	let zIndex: number = 0;
	let currentElement: HTMLElement | null = target;
	do {
		const elementZIndex: number = parseInt(
			window.getComputedStyle(currentElement).zIndex,
			10
		);

		if (!isNaN(elementZIndex)) {
			zIndex = Math.max(zIndex, elementZIndex);
		}

		currentElement = currentElement.parentElement;

		if (!currentElement) {
			throw new Error("The target could not be found in the container");
		}
	}
	while (currentElement !== container);

	return zIndex;
}

const verticalAlignmentRatioMap: { [key in VerticalAlignmentValue]: number } = {
	t: 0,
	m: 0.5,
	b: 1
};

const horizontalAlignmentRatioMap: { [key in HorizontalAlignmentValue]: number } = {
	l: 0,
	c: 0.5,
	r: 1
};

function getTargetCoordinate(
	alignment:
		| DropOverAlignmentValue
		| AbsoluteCoordinate
		| RectangleTargetAlignment,
	x: number,
	y: number,
	targetWidth: number,
	targetHeight: number
): Coordinate {
	if (typeof alignment === "string") {
		return {
			x: x + (horizontalAlignmentRatioMap[alignment[1] as HorizontalAlignmentValue] * targetWidth),
			y: y + (verticalAlignmentRatioMap[alignment[0] as VerticalAlignmentValue] * targetHeight),
		};
	}

	switch (alignment.kind) {
		case "absolute":
			return alignment.coordinate;
		case "rect":
			const {
				left,
				top,
				right,
				bottom
			} = alignment.rect;
			return getTargetCoordinate(
				alignment.alignment ?? DEFAULT_TARGET_ALIGNMENT,
				left,
				top,
				right - left,
				bottom - top
			);
		default:
			never(alignment);
	}
}

function getContentOffset(
	alignment: DropOverAlignmentValue,
	contentWidth: number,
	contentHeight: number
): Coordinate {
	return {
		x: horizontalAlignmentRatioMap[alignment[1] as HorizontalAlignmentValue] * -contentWidth,
		y: verticalAlignmentRatioMap[alignment[0] as VerticalAlignmentValue] * -contentHeight,
	};
}

type GetContentPositionOptions = {
	targetRect: DOMRect;
	targetAlignment: DropOverAlignmentValue
		| AbsoluteCoordinate
		| RectangleTargetAlignment;
	contentAlignment: DropOverAlignmentValue;
	width: number;
	height: number;
};

function getContentPosition(options: GetContentPositionOptions): Coordinate {
	const {
		targetRect,
		targetAlignment,
		contentAlignment,
		width,
		height
	} = options;

	let { x, y } = targetRect;

	// NOTE: even though our window.scrollY is expected to always be 0,
	// as we apply scroll to child containers, sometimes it could be non 0
	// One such example is ios devices.
	// Where safari automatically applies scrollY to window when virtual keyboard is shown.
	// This then breaks the calculations, as we want to calculate position relative to the document
	// and not the viewport.
	//
	// For consistency also adjust x position.
	x += window.scrollX;
	y += window.scrollY;

	const targetPosition: Coordinate = getTargetCoordinate(
		targetAlignment,
		x,
		y,
		targetRect.width,
		targetRect.height
	);

	const contentOffset: Coordinate = getContentOffset(contentAlignment, width, height);

	const position: Coordinate = {
		x: targetPosition.x + contentOffset.x,
		y: targetPosition.y + contentOffset.y,
	};

	return position;
}

type GetAvailableHeightOptions = {
	targetRect: DOMRect;
	targetAlignment: DropOverAlignmentValue | RectangleTargetAlignment;
	contentAlignment: DropOverAlignmentValue;
	positionY: number;
	containerHeight: number;
};

function getAvailableHeight(options: GetAvailableHeightOptions): number {
	const {
		targetRect,
		targetAlignment,
		contentAlignment,
		positionY,
		containerHeight
	} = options;

	if (contentAlignment[0] === "b") {
		return getTargetCoordinate(
			targetAlignment,
			targetRect.x,
			targetRect.y,
			targetRect.width,
			targetRect.height
		).y;
	}

	return containerHeight - positionY;
}

function doesFitContainerVertically(
	containerHeight: number,
	contentHeight: number,
	positionY: number
): boolean {
	return positionY >= 0
		&& positionY + contentHeight <= containerHeight;
}

function doesFitContainerHorizontally(
	containerWidth: number,
	contentWidth: number,
	positionX: number
): boolean {
	return positionX >= 0
		&& positionX + contentWidth <= containerWidth;
}

const flipVerticalAlignmentMap: { [key in VerticalAlignmentValue]: VerticalAlignmentValue } = {
	t: "b",
	m: "m",
	b: "t"
};

const flipHorizontalAlignmentMap: { [key in HorizontalAlignmentValue]: HorizontalAlignmentValue } = {
	l: "r",
	c: "c",
	r: "l"
};

function flipVertical(alignment: DropOverAlignmentValue): DropOverAlignmentValue;
function flipVertical(alignment: RectangleTargetAlignment): RectangleTargetAlignment;
function flipVertical(alignment: DropOverAlignmentValue | RectangleTargetAlignment): DropOverAlignmentValue | RectangleTargetAlignment;
function flipVertical(alignment: DropOverAlignmentValue | RectangleTargetAlignment): DropOverAlignmentValue | RectangleTargetAlignment {
	if (typeof alignment === "string") {
		return (flipVerticalAlignmentMap[alignment[0] as VerticalAlignmentValue] + alignment[1]) as DropOverAlignmentValue;
	}

	return {
		...alignment,
		alignment: flipVertical(alignment.alignment ?? DEFAULT_TARGET_ALIGNMENT)
	};
}

function flipHorizontal(alignment: DropOverAlignmentValue): DropOverAlignmentValue;
function flipHorizontal(alignment: RectangleTargetAlignment): RectangleTargetAlignment;
function flipHorizontal(alignment: DropOverAlignmentValue | RectangleTargetAlignment): DropOverAlignmentValue | RectangleTargetAlignment;
function flipHorizontal(alignment: DropOverAlignmentValue | RectangleTargetAlignment): DropOverAlignmentValue | RectangleTargetAlignment {
	if (typeof alignment === "string") {
		return (alignment[0] + flipHorizontalAlignmentMap[alignment[1] as HorizontalAlignmentValue]) as DropOverAlignmentValue;
	}

	return {
		...alignment,
		alignment: flipHorizontal(alignment.alignment ?? DEFAULT_TARGET_ALIGNMENT)
	};
}

const DEFAULT_TARGET_ALIGNMENT: DropOverAlignmentValue = "bl";

type AlignmentFlips = {
	isVertical: boolean;
	isHorizontal: boolean;
};

const NO_FLIPS: AlignmentFlips = {
	isVertical: false,
	isHorizontal: false
};
